/* Patch 24.5 — Executive Narrative Card
 * --------------------------------------------------
 * Renders the persisted AI Executive Narrative for the active
 * tenant. Reads from /api/executive-narrative/latest so the card
 * always matches the daily email and the board pack PDF (single
 * source of truth — no drift).
 *
 * Layout:
 *   [ Headline (AR + EN) + Confidence chip + Refresh button ]
 *   [ Tabs: What Changed | Risks | Recommended Actions     ]
 *   [ Footer: model + generated_at + "view history" link    ]
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import {
  Sparkles, AlertTriangle, ListChecks, Loader2,
  RefreshCw, Brain, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'what_changed', label: 'What Changed',         Icon: Sparkles,      color: 'text-emerald-600' },
  { id: 'risks',        label: 'Risks',                Icon: AlertTriangle, color: 'text-amber-600' },
  { id: 'actions',      label: 'Recommended Actions',  Icon: ListChecks,    color: 'text-indigo-600' },
];

const CONFIDENCE_BADGE = {
  high:   'bg-emerald-100 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low:    'bg-rose-100 text-rose-700 border-rose-200',
};


export default function ExecutiveNarrativeCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomposing, setRecomposing] = useState(false);
  const [activeTab, setActiveTab] = useState('what_changed');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/executive-narrative/latest');
      setData(r.data);
    } catch (e) {
      toast.error(`Narrative load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  const recompose = async () => {
    setRecomposing(true);
    try {
      const r = await axiosInstance.post('/executive-narrative/compose?force=true');
      setData(r.data);
      toast.success('Narrative recomposed.');
    } catch (e) {
      toast.error(`Recompose failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setRecomposing(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <section className="bg-white rounded-xl border border-slate-200 p-6"
               data-testid="executive-narrative-loading">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="animate-spin" />
          <span>Composing executive narrative…</span>
        </div>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="bg-white rounded-xl border border-slate-200 p-6 flex items-center justify-between"
               data-testid="executive-narrative-empty">
        <div className="flex items-center gap-3 text-slate-500">
          <Brain size={16} />
          <span className="text-sm">No narrative available for today yet.</span>
        </div>
        <button
          data-testid="narrative-empty-retry-btn"
          onClick={recompose}
          disabled={recomposing}
          className="text-xs font-semibold text-indigo-700 hover:text-indigo-800 inline-flex items-center gap-1.5"
        >
          {recomposing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Generate now
        </button>
      </section>
    );
  }

  const items = data[activeTab] || [];

  return (
    <section
      data-testid="executive-narrative-card"
      className="bg-gradient-to-br from-white via-slate-50 to-indigo-50/30 rounded-xl border border-slate-200 shadow-sm overflow-hidden"
    >
      {/* Header */}
      <header className="px-5 pt-4 pb-3 border-b border-slate-200/70">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
              <Brain size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-bold mb-0.5">
                AI Executive Narrative
              </div>
              <h3 className="text-base font-bold text-slate-900 truncate"
                  data-testid="narrative-headline-en"
                  title={data.headline_en}>
                {data.headline_en || 'Today\'s governance posture.'}
              </h3>
              <p dir="rtl" lang="ar"
                 data-testid="narrative-headline-ar"
                 className="text-sm text-slate-600 mt-0.5 truncate text-right"
                 title={data.headline_ar}>
                {data.headline_ar}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              data-testid="narrative-confidence-chip"
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${CONFIDENCE_BADGE[data.confidence] || CONFIDENCE_BADGE.medium}`}
            >
              {data.confidence} conf
            </span>
            <button
              data-testid="narrative-refresh-btn"
              onClick={recompose}
              disabled={recomposing}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded px-2 py-1 disabled:opacity-50"
              title="Force recompose"
            >
              {recomposing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-5 pt-3 flex items-center gap-1">
        {TABS.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`narrative-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded transition-colors ${
                active
                  ? 'bg-slate-900 text-white shadow'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <t.Icon size={12} />
              {t.label}
              <span className={`text-[10px] ${active ? 'opacity-70' : 'opacity-50'}`}>
                ({(data[t.id] || []).length})
              </span>
            </button>
          );
        })}
      </div>

      {/* Bullets */}
      <ul data-testid={`narrative-list-${activeTab}`}
          className="px-5 py-4 space-y-2">
        {items.length === 0 ? (
          <li className="text-sm text-slate-500 italic">
            No items in this section.
          </li>
        ) : (
          items.map((bullet, idx) => {
            // Patch 26 Phase 1: actions are structured dicts; what_changed
            // / risks remain strings. Render either gracefully.
            const text = typeof bullet === 'string'
              ? bullet
              : (bullet?.text || JSON.stringify(bullet));
            const urgency = typeof bullet === 'object' ? bullet?.urgency_label : null;
            return (
              <li
                key={idx}
                data-testid={`narrative-${activeTab}-item-${idx}`}
                className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed"
              >
                <ChevronRight size={14}
                  className={`mt-0.5 flex-shrink-0 ${TABS.find(t => t.id === activeTab).color}`} />
                <span className="flex-1">
                  {text}
                  {urgency && (
                    <span className={`ml-2 inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${
                      urgency === 'critical' ? 'bg-rose-100 text-rose-700 border-rose-200' :
                      urgency === 'high'     ? 'bg-amber-100 text-amber-700 border-amber-200' :
                      urgency === 'medium'   ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                      'bg-slate-100 text-slate-600 border-slate-200'
                    }`}>
                      {urgency}
                    </span>
                  )}
                </span>
              </li>
            );
          })
        )}
      </ul>

      {/* Footer */}
      <footer className="px-5 py-2 border-t border-slate-200/70 bg-white/50 flex items-center justify-between text-[10px] text-slate-500">
        <span className="font-mono">
          {data.model} · {data.input_digest?.slice(0, 8)}
        </span>
        <span title={data.generated_at}>
          Generated {formatRel(data.generated_at)}
        </span>
      </footer>
    </section>
  );
}


function formatRel(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const ms = Date.now() - t;
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000)  return `${Math.round(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
    return `${Math.round(ms / 86_400_000)}d ago`;
  } catch {
    return String(iso).slice(0, 16);
  }
}
